import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../../services/api';
import { ArrowLeft, Plus, Edit2, Trash2, AlertCircle, Package, TrendingDown } from 'lucide-react';
import '../../../styles/admin/ERPInventory.css';


const ERPInventory = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [adjustingProduct, setAdjustingProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('Tous');
  const [formData, setFormData] = useState({
    nom: '',
    categorie: 'Engrais',
    description: '',
    unite: 'litre',
    prix_unitaire: '',
    quantite_stock: '',
    seuil_reapprovisionnement: ''
  });
  const [adjustQuantity, setAdjustQuantity] = useState(0);
  const [adjustReason, setAdjustReason] = useState('');

  const categories = ['Tous', 'Engrais', 'Insecticide', 'Herbicide', 'Antigerminant', 'Chaux'];
  const unites = ['litre', 'sac', 'kg', 'unité'];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/api/erp/products', {
      });
      setProducts(response.data.products || []);
    } catch (error) {
      if (error.response?.status === 401) {
        
        navigate('/auth/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setFormData({
      nom: '',
      categorie: 'Engrais',
      description: '',
      unite: 'litre',
      prix_unitaire: '',
      quantite_stock: '',
      seuil_reapprovisionnement: ''
    });
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      nom: product.nom,
      categorie: product.categorie || 'Engrais',
      description: product.description || '',
      unite: product.unite || 'litre',
      prix_unitaire: product.prix_unitaire,
      quantite_stock: product.quantite_stock,
      seuil_reapprovisionnement: product.seuil_reapprovisionnement || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProduct) {
        await api.put(
          `/api/erp/products/${editingProduct.id}`,
          formData,
        );
      } else {
        await api.post(
          '/api/erp/products',
          formData,
        );
      }

      setShowModal(false);
      fetchProducts();
    } catch (error) {
      alert('Erreur lors de la sauvegarde du produit');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit?')) {
      return;
    }

    try {
      await api.delete(`/api/erp/products/${productId}`, {
      });
      fetchProducts();
    } catch (error) {
      alert('Erreur lors de la suppression du produit');
    }
  };

  const handleAdjustStock = (product) => {
    setAdjustingProduct(product);
    setAdjustQuantity(0);
    setAdjustReason('');
    setShowAdjustModal(true);
  };

  const handleSubmitAdjustment = async (e) => {
    e.preventDefault();

    try {
      await api.post(
        `/api/erp/products/${adjustingProduct.id}/adjust-stock`,
        {
          quantite_adjustment: parseFloat(adjustQuantity),
          raison: adjustReason
        },
      );

      setShowAdjustModal(false);
      fetchProducts();
    } catch (error) {
      alert('Erreur lors de l\'ajustement du stock');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Engrais': '🌱',
      'Insecticide': '🐛',
      'Herbicide': '🌿',
      'Antigerminant': '🚫',
      'Chaux': '🪨'
    };
    return icons[category] || '📦';
  };

  const calculateValeurStock = (product) => {
    const prix = parseFloat(product.prix_unitaire) || 0;
    const quantite = parseFloat(product.quantite_stock) || 0;
    return prix * quantite;
  };

  const calculateQuantiteReappro = (product) => {
    const seuil = parseFloat(product.seuil_reapprovisionnement) || 0;
    const stock = parseFloat(product.quantite_stock) || 0;
    return stock < seuil ? seuil - stock : 0;
  };

  const needsReorder = (product) => {
    const stock = parseFloat(product.quantite_stock) || 0;
    const seuil = parseFloat(product.seuil_reapprovisionnement) || 0;
    return stock < seuil;
  };

  const filteredProducts = filterCategory === 'Tous' 
    ? products 
    : products.filter(p => p.categorie === filterCategory);

  const valeurTotale = filteredProducts.reduce((sum, p) => sum + calculateValeurStock(p), 0);
  const produitsAReappro = filteredProducts.filter(needsReorder).length;

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="erp-inventory-page">
      <div className="erp-inventory-header">
        <div className="erp-inventory-header-content">
          <Link to="/admin/erp" className="erp-back-link">
            <ArrowLeft size={20} />
            Retour au dashboard
          </Link>
          <div className="header-title-section">
            <h1><Package size={32} /> Inventaire</h1>
            <p>Gestion des produits et stock</p>
          </div>
        </div>
      </div>

      <div className="erp-inventory-content">
        {/* Stats Bar */}
        <div className="inventory-stats">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-info">
              <div className="stat-value">{filteredProducts.length}</div>
              <div className="stat-label">Produits</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-value">
                {valeurTotale.toLocaleString('fr-CA', {
                  style: 'currency',
                  currency: 'CAD',
                  minimumFractionDigits: 0
                })}
              </div>
              <div className="stat-label">Valeur totale</div>
            </div>
          </div>
          <div className={`stat-card ${produitsAReappro > 0 ? 'alert' : ''}`}>
            <div className="stat-icon">
              {produitsAReappro > 0 ? <AlertCircle size={28} /> : '✓'}
            </div>
            <div className="stat-info">
              <div className="stat-value">{produitsAReappro}</div>
              <div className="stat-label">À réapprovisionner</div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="inventory-toolbar">
          <div className="filter-section">
            <label>Catégorie:</label>
            <select 
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="category-filter"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <button className="btn-primary" onClick={handleCreate}>
            <Plus size={20} />
            Nouveau produit
          </button>
        </div>

        {/* Inventory Table */}
        <div className="inventory-table-container">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Produit</th>
                <th>Catégorie</th>
                <th>Unité</th>
                <th className="text-right">Prix unit.</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Valeur stock</th>
                <th className="text-right">Seuil</th>
                <th className="text-right">À réappro.</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="9" className="empty-state">
                    <Package size={48} color="#ccc" />
                    <p>Aucun produit dans cette catégorie</p>
                  </td>
                </tr>
              ) : (
                filteredProducts.map(product => {
                  const valeurStock = calculateValeurStock(product);
                  const quantiteReappro = calculateQuantiteReappro(product);
                  const isLowStock = needsReorder(product);

                  return (
                    <tr key={product.id} className={isLowStock ? 'low-stock' : ''}>
                      <td>
                        <div className="product-name">
                          <strong>{product.nom}</strong>
                          {product.description && (
                            <small>{product.description}</small>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="category-badge">
                          {getCategoryIcon(product.categorie)} {product.categorie}
                        </span>
                      </td>
                      <td>{product.unite}</td>
                      <td className="text-right">
                        {(parseFloat(product.prix_unitaire) || 0).toLocaleString('fr-CA', {
                          style: 'currency',
                          currency: 'CAD'
                        })}
                      </td>
                      <td className="text-right">
                        <span className={isLowStock ? 'stock-low' : 'stock-ok'}>
                          {parseFloat(product.quantite_stock) || 0}
                        </span>
                      </td>
                      <td className="text-right font-semibold">
                        {valeurStock.toLocaleString('fr-CA', {
                          style: 'currency',
                          currency: 'CAD'
                        })}
                      </td>
                      <td className="text-right">{parseFloat(product.seuil_reapprovisionnement) || 0}</td>
                      <td className="text-right">
                        {quantiteReappro > 0 ? (
                          <span className="reappro-badge">
                            {quantiteReappro} {isLowStock && '⚠️'}
                          </span>
                        ) : (
                          <span className="reappro-ok">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="action-buttons">
                          <button
                            className="btn-icon"
                            onClick={() => handleAdjustStock(product)}
                            title="Ajuster stock"
                          >
                            <TrendingDown size={16} />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => handleEdit(product)}
                            title="Modifier"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="btn-icon btn-icon-danger"
                            onClick={() => handleDelete(product.id)}
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Créer/Modifier Produit */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-group">
                <label>Nom du produit *</label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  placeholder="Ex: FOLIUM 14-3-7"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie *</label>
                  <select
                    value={formData.categorie}
                    onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                    required
                  >
                    {categories.filter(c => c !== 'Tous').map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Unité *</label>
                  <select
                    value={formData.unite}
                    onChange={(e) => setFormData({...formData, unite: e.target.value})}
                    required
                  >
                    {unites.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="2"
                  placeholder="Description du produit..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Prix unitaire ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={formData.prix_unitaire}
                    onChange={(e) => setFormData({...formData, prix_unitaire: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Quantité en stock *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={formData.quantite_stock}
                    onChange={(e) => setFormData({...formData, quantite_stock: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Seuil de réapprovisionnement</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.seuil_reapprovisionnement}
                    onChange={(e) => setFormData({...formData, seuil_reapprovisionnement: e.target.value})}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingProduct ? 'Mettre à jour' : 'Créer le produit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ajustement Stock */}
      {showAdjustModal && (
        <div className="modal-overlay" onClick={() => setShowAdjustModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajuster le stock</h2>
              <button className="modal-close" onClick={() => setShowAdjustModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmitAdjustment} className="adjust-form">
              <div className="product-info-box">
                <h3>{adjustingProduct?.nom}</h3>
                <p>Stock actuel: <strong>{adjustingProduct?.quantite_stock} {adjustingProduct?.unite}</strong></p>
              </div>

              <div className="form-group">
                <label>Ajustement (+ ou -) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={adjustQuantity}
                  onChange={(e) => setAdjustQuantity(e.target.value)}
                  placeholder="Ex: +10 ou -5"
                />
                <small>Nouveau stock: {((adjustingProduct?.quantite_stock || 0) + parseFloat(adjustQuantity || 0)).toFixed(2)} {adjustingProduct?.unite}</small>
              </div>

              <div className="form-group">
                <label>Raison *</label>
                <textarea
                  required
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  rows="3"
                  placeholder="Ex: Réception commande, Inventaire physique, Utilisation projet..."
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAdjustModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Confirmer l'ajustement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPInventory;
